import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { PIXEL_COLORS } from '../lib/pixellib';
import PixelLogo from './containers/PixelLogo';
import { usePixelManager } from '../contexts/PixelContext';
import './PixelIcon.css';

const PixelIcon = ({ characterId }) => {
  const pixels = useSelector(state => Object.values(state.pixels.list));
  const [testing, setTesting] = useState(false);
  const { forceConnectionTest, pairPixel } = usePixelManager();
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Manual connection test (run only on click or after pairing)
  const handleForceConnectionTest = async () => {
    if (testing) return; // Prevent concurrent tests
    setTesting(true);

    try {
      await forceConnectionTest();
    } catch (error) {
      // This is likely a real connection failure, not a Context issue
      // The connection status will be updated by the test function
      console.error('Failed to force pixel connection test:', error);
    }

    if (isMounted.current) {
      setTesting(false);
    }
  };

  // After pairing/re-pairing, run a connection test and log debug info
  const handlePair = async () => {
    try {
      await pairPixel(characterId);
    } catch (error) {
      // PixelManager handles all error cases appropriately
      // User cancellations (NotFoundError) are ignored
      // Real pairing failures are logged and handled by the manager
      if (error.name !== 'NotFoundError') {
        console.error('An unexpected error occurred during pairing:', error);
      }
    }
  };

  // Use connectionStatus from Redux to determine icon state
  const isDisconnected = pixels.length > 0 && pixels.some(p => p.connectionStatus === false);

  // Pixel icon click: if disconnected, pair; if connected, blink and test connection
  const handlePixelIconClick = async () => {
    if (isDisconnected) {
      handlePair();
    } else {
      // Blink all paired Pixels with a visible color (green)
      await Promise.all(pixels.map(async (pixel) => {
        if (pixel.pixel && typeof pixel.pixel.blink === 'function') {
          try {
            await pixel.pixel.blink(PIXEL_COLORS.GREEN.pixel_constant);
          } catch (e) {
            // Ignore blink errors here
          }
        }
      }));
      // Then run the connection test
      handleForceConnectionTest();
    }
  };

  // Don't render if no pixels are paired
  if (pixels.length === 0) {
    return null;
  }

  return (
    <div className='Header__control Header__control--pixel'>
      <div
        className={`PixelHeaderIcon${isDisconnected ? ' PixelHeaderIcon--disconnected' : ''}`}
        title={isDisconnected ? 'Re-pair Pixel' : 'Pixel'}
        onClick={handlePixelIconClick}
      >
        <PixelLogo width={35} height={35} />
      </div>
      <p>Pixel</p>
    </div>
  );
};

export default PixelIcon;